import React, { useState, useMemo } from "react";
import { Card, Row, Col, Select, Table, Typography } from "antd";
import { FileOutlined, SolutionOutlined, FileExcelOutlined, } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { extractFilesList, getProjects, getMonthsByProject, } from "./file-utils";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
const { Title, Text } = Typography;

export function FileManagementDashboard({ data }) {
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const allFiles = useMemo(() => extractFilesList(data), [data]);
  const projects = useMemo(() => getProjects(data), [data]);
  const months = useMemo(() => {
    if (selectedProject === "all") return [];
    return getMonthsByProject(data, selectedProject);
  }, [data, selectedProject]);

  const filteredFiles = useMemo(() => {
    return allFiles.filter((file) => {
      if (selectedProject !== "all" && file.project !== selectedProject)
        return false;
      if (selectedMonth !== "all" && file.month !== selectedMonth)
        return false;
      if (userFilter === "with-user" && !file.participante) return false;
      if (userFilter === "without-user" && file.participante) return false;
      return true;
    });
  }, [allFiles, selectedProject, selectedMonth, userFilter]);

  const stats = useMemo(() => {
    const totalFiles = filteredFiles.length;
    const filesWithUser = filteredFiles.filter((f) => f.participante).length;
    const filesWithoutUser = totalFiles - filesWithUser;

    return { totalFiles, filesWithUser, filesWithoutUser };
  }, [filteredFiles]);
  function cleanFileName(name) {
    return name.replace(/^[0-9]+-/, "");
  }

  const columns = [
    {
      title: "Archivo",
      dataIndex: "name",
      key: "name",
      render: (text) => cleanFileName(text),
    },
    {
      title: "Proyecto",
      dataIndex: "project",
      key: "project",
    },
    {
      title: "Mes",
      dataIndex: "month",
      key: "month",
    },
    {
      title: "Participante",
      key: "participante",
      render: (_, record) =>
        record.participante ? record.participante.nombre_participante : "—",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, overflowX: "hidden", }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card
            hoverable
            style={{
              height: "140px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "16px",
              borderRadius: "8px",
            }}
          >
            <Row align="middle" justify="center" style={{ height: "100%" }}>
              <Col style={{ textAlign: "center" }}>
                <Text strong>Total de archivos</Text>
                <div style={{ height: 8, margin: "0 auto" }}>
                  <div
                    style={{
                      width: 20,
                      height: 4,
                      backgroundColor: "#3bbc37ff",
                      borderRadius: 2,
                      margin: "0 auto",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: 28,
                    fontWeight: "bold",
                    color: "#338BC7",
                    marginTop: 12,
                  }}
                >
                  <FileOutlined style={{ color: "#338BC7", fontSize: 32 }} />
                  <span>{stats.totalFiles}</span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            hoverable
            style={{
              height: "140px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "16px",
              borderRadius: "8px",
            }}
          >
            <Row align="middle" justify="center" style={{ height: "100%" }}>
              <Col style={{ textAlign: "center" }}>
                <Text strong>Archivos con participante</Text>
                <div style={{ height: 8, margin: "0 auto" }}>
                  <div
                    style={{
                      width: 20,
                      height: 4,
                      backgroundColor: "#3bbc37ff",
                      borderRadius: 2,
                      margin: "0 auto",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: 28,
                    fontWeight: "bold",
                    color: "#338BC7",
                    marginTop: 12,
                  }}
                >
                  <SolutionOutlined style={{ color: "#338BC7", fontSize: 32 }} />
                  <span>{stats.filesWithUser}</span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            hoverable
            style={{
              height: "140px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "16px",
              borderRadius: "8px",
            }}
          >
            <Row align="middle" justify="center" style={{ height: "100%" }}>
              <Col style={{ textAlign: "center" }}>
                <Text strong>Archivos sin participante</Text>
                <div style={{ height: 8, margin: "0 auto" }}>
                  <div
                    style={{
                      width: 20,
                      height: 4,
                      backgroundColor: "#3bbc37ff",
                      borderRadius: 2,
                      margin: "0 auto",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: 28,
                    fontWeight: "bold",
                    color: "#338BC7",
                    marginTop: 12,
                  }}
                >
                  <FileExcelOutlined style={{ color: "#338BC7", fontSize: 32 }} />
                  <span>{stats.filesWithoutUser}</span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Card
        style={{
          padding: "10px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          backgroundColor: "#042956",
          marginBottom: "24px",
          border: "none",
          width: "100%"
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "16px"
        }}>
          <Title level={2} style={{
            margin: 0,
            color: "#ffffff",
            fontSize: "20px",
            fontWeight: 600
          }}>
            <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: "12px", opacity: 0.9 }} />
            Filtro de búsqueda
          </Title>
        </div>

        <div style={{
          display: "flex",
          gap: "16px",
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              value={selectedProject}
              onChange={(value) => {
                setSelectedProject(value);
                setSelectedMonth("all");
              }}
              style={{ width: "100%", marginTop: 8 }}
              options={[
                { label: "Todos", value: "all" },
                ...projects.map((p) => ({ label: p, value: p })),
              ]}
            />
          </div>

          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              disabled={selectedProject === "all"}
              value={selectedMonth}
              onChange={(value) => setSelectedMonth(value)}
              style={{ width: "100%", marginTop: 8, backgroundColor: "#c1c1c1ff", borderRadius: 4 }}
              options={[
                { label: "Todos", value: "all" },
                ...months.map((m) => ({ label: m, value: m })),
              ]}
            />
          </div>

          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              value={userFilter}
              onChange={(value) => setUserFilter(value)}
              style={{ width: "100%", marginTop: 8 }}
              options={[
                { label: "Todos", value: "all" },
                { label: "Con participante", value: "with-user" },
                { label: "Sin participante", value: "without-user" },
              ]}
            />
          </div>
        </div>
      </Card>
      <Table
        columns={columns}
        dataSource={filteredFiles}
        rowKey="path"
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} archivos`
        }} scroll={{
          x: 1600,
          y: 600
        }} size="middle"
        sticky={true}
        style={{
          fontSize: '14px'
        }}
      />
    </div>
  );
}
